import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { adminFetch } from "../adminApi";
import {
  CATEGORIES,
  adelaideMonth,
  historyCsv,
  monthLabel,
  pctChange,
  records,
  yearOnYear,
  yearToDate,
  yearTotals,
  MONTH_NAMES,
} from "./visitorHistory";

/**
 * Analytics → History: monthly visitors since 2015. Months up to the booking
 * system's start come from the archive spreadsheet (editable here, and
 * re-importable); later months from bookings and the group log.
 * GET /api/admin/stats/monthly, PUT /api/admin/visitor-history/{month}/{cat},
 * POST /api/admin/visitor-history/import (dry run, then save).
 */

const FIRST_MONTH = "2015-01";
const TOOLTIP_STYLE = {
  fontFamily: '"Mulish", sans-serif',
  fontSize: 13,
  borderRadius: 9,
  border: "1px solid #e5dfd3",
  boxShadow: "0 10px 26px rgba(0,0,0,.1)",
  background: "#fff",
};
const AXIS_TICK = { fontSize: 11, fill: "#9a958c" };
const YEAR_LINE_COLORS = ["#f15a24", "#2a2018", "#1bab88", "#2596c9", "#7c4dff", "#d97706", "#c0392b", "#9a958c"];
const nf = new Intl.NumberFormat("en-AU");
const fmt = (n) => (n == null ? "—" : nf.format(n));

function Delta({ now, before, label }) {
  const pct = pctChange(now, before);
  if (pct == null) return <div className="vh-kpi-sub">{label}: —</div>;
  const up = pct >= 0;
  return (
    <div className={"vh-kpi-sub " + (up ? "is-up" : "is-down")}>
      {up ? "▲" : "▼"} {Math.abs(pct).toFixed(0)}% vs {label} ({fmt(before)})
    </div>
  );
}

export default function RAdminVisitorHistory({ navigate, toast }) {
  const currentMonth = adelaideMonth();
  const currentYear = Number(currentMonth.slice(0, 4));
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("all");
  const [compareYears, setCompareYears] = useState([currentYear, currentYear - 1, 2019]);
  const [tableYear, setTableYear] = useState(currentYear);
  const [editing, setEditing] = useState(null); // {month, key, value}
  const [preview, setPreview] = useState(null); // dry-run import result + file
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await adminFetch(`/api/admin/stats/monthly?start=${FIRST_MONTH}&end=${currentMonth}`, { navigate });
      if (!res.ok) throw new Error("Couldn't load visitor history");
      setData(await res.json());
      setError("");
    } catch (e) {
      if (e && e.name === "AdminAuthError") return;
      setError(e.message || "Couldn't load visitor history");
    }
  }, [currentMonth, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => (data ? data.months : []), [data]);
  const years = useMemo(() => yearTotals(rows, currentMonth), [rows, currentMonth]);
  const allYears = years.map((y) => y.year);
  const ytd = useMemo(() => (rows.length ? yearToDate(rows, currentMonth, category, false) : null), [rows, currentMonth, category]);
  const recs = useMemo(() => records(rows, years, category), [rows, years, category]);
  const yoy = useMemo(() => yearOnYear(rows, [...compareYears].sort(), category), [rows, compareYears, category]);
  const tableRows = rows.filter((r) => r.month.startsWith(String(tableYear)));
  const yearChart = years.map((y) => ({ ...y, label: y.partial ? `${y.year} YTD` : String(y.year) }));
  const shownCats = category === "all" ? CATEGORIES : CATEGORIES.filter((c) => c.key === category);

  const toggleYear = (year) =>
    setCompareYears((ys) => (ys.includes(year) ? ys.filter((y) => y !== year) : [...ys, year].slice(-8)));

  const exportCsv = () => {
    const blob = new Blob([historyCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `umoona-visitors-${FIRST_MONTH}-to-${currentMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const canEdit = (row, key) => row.from === "archive" || (key === "ghan" && row.estimated);

  const saveCell = async () => {
    if (!editing) return;
    const raw = String(editing.value).trim();
    const guests = raw === "" ? null : Number(raw);
    if (guests != null && (!Number.isInteger(guests) || guests < 0)) {
      toast.error("Enter a whole number, or leave it empty for “not recorded”");
      return;
    }
    try {
      const res = await adminFetch(`/api/admin/visitor-history/${editing.month}/${editing.key}`, {
        method: "PUT",
        body: JSON.stringify({ guests }),
        navigate,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Couldn't save");
      }
      setEditing(null);
      toast.success(`${monthLabel(editing.month)} saved`);
      load();
    } catch (e) {
      if (e && e.name !== "AdminAuthError") toast.error(e.message);
    }
  };

  const runImport = async (file, dryRun) => {
    const form = new FormData();
    form.append("file", file);
    form.append("dry_run", dryRun ? "true" : "false");
    setImporting(true);
    try {
      const res = await adminFetch("/api/admin/visitor-history/import", { method: "POST", body: form, navigate });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Couldn't read that spreadsheet");
      if (dryRun) setPreview({ ...body, file });
      else {
        setPreview(null);
        toast.success(`Imported: ${body.changes.new} new, ${body.changes.changed} changed`);
        load();
      }
    } catch (e) {
      if (e && e.name !== "AdminAuthError") toast.error(e.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (error) {
    return (
      <div className="adm-card vh-error">
        {error}{" "}
        <button type="button" className="adm-btn adm-btn--ghost" onClick={load}>
          Try again
        </button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="adm-loading">
        <div className="adm-spinner" />
        Loading visitor history...
      </div>
    );
  }

  const monthsName = ytd && ytd.lastMonth > 0 ? `Jan–${MONTH_NAMES[ytd.lastMonth - 1]}` : "";

  return (
    <div className="vh">
      {/* Category + actions */}
      <div className="vh-bar">
        <div className="vh-pills" role="tablist" aria-label="Visitor type">
          {[{ key: "all", label: "All visitors", color: "#2a2018" }, ...CATEGORIES].map((c) => (
            <button
              key={c.key}
              type="button"
              role="tab"
              aria-selected={category === c.key}
              className={"vh-pill" + (category === c.key ? " is-on" : "")}
              style={category === c.key ? { background: c.color, borderColor: c.color } : undefined}
              onClick={() => setCategory(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="vh-actions">
          <button type="button" className="adm-btn adm-btn--ghost" onClick={exportCsv}>
            ⤓ Export CSV
          </button>
          <button type="button" className="adm-btn adm-btn--ghost" disabled={importing} onClick={() => fileRef.current && fileRef.current.click()}>
            {importing ? "Reading…" : "⤒ Import spreadsheet"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(e) => e.target.files[0] && runImport(e.target.files[0], true)}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="adm-grid4 vh-kpis">
        <div className="adm-stat">
          <div className="adm-stat-label">{currentYear} so far ({monthsName})</div>
          <div className="adm-stat-value">{fmt(ytd && ytd.thisYear)}</div>
          <Delta now={ytd && ytd.thisYear} before={ytd && ytd.lastYear} label={`${monthsName} ${currentYear - 1}`} />
        </div>
        <div className="adm-stat">
          <div className="adm-stat-label">Pre-COVID average ({monthsName})</div>
          <div className="adm-stat-value">{fmt(ytd && ytd.average)}</div>
          <Delta now={ytd && ytd.thisYear} before={ytd && ytd.average} label="2015–19 average" />
        </div>
        <div className="adm-stat">
          <div className="adm-stat-label">Best year</div>
          <div className="adm-stat-value">{recs.bestYear ? recs.bestYear.year : "—"}</div>
          <div className="vh-kpi-sub">{recs.bestYear ? `${fmt(recs.bestYear.value)} visitors` : ""}</div>
        </div>
        <div className="adm-stat">
          <div className="adm-stat-label">Busiest month</div>
          <div className="adm-stat-value">{recs.bestMonth ? monthLabel(recs.bestMonth.month) : "—"}</div>
          <div className="vh-kpi-sub">{recs.bestMonth ? `${fmt(recs.bestMonth.value)} visitors` : ""}</div>
        </div>
      </div>

      {/* Years at a glance */}
      <div className="adm-card vh-card">
        <div className="anx-chart-title">Visitors per year</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={yearChart} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#f0ece3" vertical={false} />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => nf.format(v)} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => [nf.format(v), name]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {shownCats.map((c) => (
              <Bar key={c.key} dataKey={c.key} name={c.label} stackId="y" fill={c.color} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Year on year */}
      <div className="adm-card vh-card">
        <div className="vh-card-head">
          <div className="anx-chart-title">Month by month, year on year</div>
          <div className="vh-years">
            {allYears.map((y) => (
              <button
                key={y}
                type="button"
                className={"vh-year" + (compareYears.includes(y) ? " is-on" : "")}
                onClick={() => toggleYear(y)}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={yoy} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#f0ece3" vertical={false} />
            <XAxis dataKey="month" tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} tickFormatter={(v) => nf.format(v)} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => [v == null ? "—" : nf.format(v), name]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {[...compareYears].sort().map((y, i) => (
              <Line
                key={y}
                type="monotone"
                dataKey={y}
                name={String(y)}
                stroke={YEAR_LINE_COLORS[i % YEAR_LINE_COLORS.length]}
                strokeWidth={y === currentYear ? 3 : 2}
                dot={{ r: 2.5 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="adm-card vh-card">
        <div className="vh-card-head">
          <div className="anx-chart-title">Monthly figures</div>
          <select className="adm-input adm-input--sm vh-yearselect" value={tableYear} onChange={(e) => setTableYear(Number(e.target.value))}>
            {[...allYears].reverse().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <div className="adm-tablewrap">
          <table className="adm-table vh-table">
            <thead>
              <tr>
                <th>Month</th>
                {CATEGORIES.map((c) => (
                  <th key={c.key} className="vh-num">
                    <span className="vh-dot" style={{ background: c.color }} />
                    {c.label}
                  </th>
                ))}
                <th className="vh-num">Total</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.month}>
                  <td className="vh-month">{monthLabel(row.month)}</td>
                  {CATEGORIES.map((c) => {
                    const isEditing = editing && editing.month === row.month && editing.key === c.key;
                    const editable = canEdit(row, c.key);
                    return (
                      <td key={c.key} className={"vh-num" + (editable ? " is-editable" : "")} title={(row.notes && row.notes[c.key]) || undefined}>
                        {isEditing ? (
                          <input
                            autoFocus
                            className="vh-cellinput"
                            inputMode="numeric"
                            value={editing.value}
                            onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveCell();
                              if (e.key === "Escape") setEditing(null);
                            }}
                            onBlur={saveCell}
                          />
                        ) : editable ? (
                          <button
                            type="button"
                            className="vh-cellbtn"
                            onClick={() => setEditing({ month: row.month, key: c.key, value: row[c.key] == null ? "" : row[c.key] })}
                          >
                            {fmt(row[c.key])}
                          </button>
                        ) : (
                          fmt(row[c.key])
                        )}
                      </td>
                    );
                  })}
                  <td className="vh-num vh-total">{fmt(row.total)}</td>
                  <td>
                    <span className={"vh-badge vh-badge--" + row.from}>{row.from === "archive" ? "Archive" : "Live"}</span>
                    {row.estimated && (
                      <span className="vh-badge vh-badge--est" title="Ghan comes from the archive spreadsheet and is taken out of “Other tour groups”, where those groups were logged">
                        Estimated
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="vh-note">
          Archive months (to {monthLabel(data.history_until)}) come from the yearly tours spreadsheet. Click a figure to
          correct it, or leave it empty for “not recorded” (—), which isn't counted as 0. Later months come from
          bookings and the Manual Tour Log. Until Ghan groups are logged as “Ghan”, the spreadsheet's Ghan figure is used
          and taken out of “Other tour groups” (Estimated).
        </p>
      </div>

      {/* Import preview */}
      {preview && (
        <div className="adm-modal-overlay" onClick={() => !importing && setPreview(null)}>
          <div className="adm-modal adm-modal--wide" role="dialog" aria-label="Import preview" onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-title">Import {preview.file.name}?</div>
            <p className="vh-note">
              {preview.cells} monthly figures from {monthLabel(preview.first_month)} to {monthLabel(preview.last_month)}:{" "}
              <strong>{preview.changes.new}</strong> new, <strong>{preview.changes.changed}</strong> changed,{" "}
              {preview.changes.unchanged} unchanged.
            </p>
            {preview.warnings.length > 0 && (
              <ul className="vh-warnings">
                {preview.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            )}
            <div className="adm-tablewrap">
              <table className="adm-table vh-table">
                <thead>
                  <tr>
                    <th>Year</th>
                    {CATEGORIES.map((c) => (
                      <th key={c.key} className="vh-num">
                        {c.label}
                      </th>
                    ))}
                    <th className="vh-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(preview.yearly).map(([year, v]) => (
                    <tr key={year}>
                      <td>{year}</td>
                      {CATEGORIES.map((c) => (
                        <td key={c.key} className="vh-num">
                          {fmt(v[c.key])}
                        </td>
                      ))}
                      <td className="vh-num vh-total">{fmt(CATEGORIES.reduce((a, c) => a + (v[c.key] || 0), 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adm-modal-actions">
              <button type="button" className="adm-btn adm-btn--ghost" disabled={importing} onClick={() => setPreview(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="adm-btn adm-btn--ember"
                disabled={importing || preview.changes.new + preview.changes.changed === 0}
                onClick={() => runImport(preview.file, false)}
              >
                {importing ? "Importing…" : preview.changes.new + preview.changes.changed === 0 ? "Nothing to change" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
